import { Box, Text } from '@factorialco/deprecated-design-system'
import moment, { Moment } from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import Select from 'modules/core/components/Select'
import { i18nDateToString, stringToDate } from 'modules/core/lib/dates'

interface TimeOption {
  label: string
  value: string
}

interface DateTimeSelectorProps {
  date: Date | null
  placeholder?: string
  readonly?: boolean
  minDate?: Date
  maxDate?: Date
  mandatory?: boolean
  error?: string
  onChange?: (date: Date) => void
  fullWidth?: boolean
  restrictToFutureTimes?: boolean
  biggerTimeSelector?: boolean
}

const DEFAULT_HOUR = 8
const DEFAULT_MINUTE = 0
const TIME_SLOT_INTERVAL = 30

const generateAllTimeOptions = (): string[] => {
  return [...Array(24)].flatMap((_, hour) =>
    [0, TIME_SLOT_INTERVAL].map(
      (minute) => `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
    )
  )
}

const parseTimeString = (timeString: string): { hour: number; minute: number } | null => {
  const parts = timeString.split(':')
  const hour = Number(parts[0])
  const minute = Number(parts[1])

  if (isNaN(hour) || isNaN(minute)) {
    return null
  }

  return { hour, minute }
}

const isTimeAvailable = (timeString: string, selectedDate: Date): boolean => {
  const today = moment()
  const selectedMoment = moment(selectedDate)

  if (!selectedMoment.isSame(today, 'day')) {
    return true
  }

  const parsedTime = parseTimeString(timeString)
  if (!parsedTime) {
    return false
  }

  const { hour, minute } = parsedTime
  const targetTime = moment().hour(hour).minute(minute).second(0).millisecond(0)
  return targetTime.isSameOrAfter(today)
}

const getAvailableTimeOptions = (
  selectedDate: Date | null,
  restrictToFutureTimes = false
): string[] => {
  const allTimeOptions = generateAllTimeOptions()

  if (!selectedDate || !restrictToFutureTimes) {
    return allTimeOptions
  }

  return allTimeOptions.filter((timeOption) => isTimeAvailable(timeOption, selectedDate))
}

const getFirstAvailableTime = (availableOptions: string[]): { hour: number; minute: number } => {
  const firstOption = availableOptions?.[0]
  return parseTimeString(firstOption || '') || { hour: DEFAULT_HOUR, minute: DEFAULT_MINUTE }
}

const roundToNearest30Minutes = (date: Moment): Moment => {
  const minutes = date.minutes()
  const newDate = moment(date).startOf('minute')

  if (minutes >= 15 && minutes < 45) {
    return newDate.startOf('hour').add(TIME_SLOT_INTERVAL, 'minutes')
  }

  if (minutes >= 45) {
    return newDate.startOf('hour').add(60, 'minutes')
  }

  return newDate.startOf('hour')
}

export const DateTimeSelector = ({
  mandatory,
  date,
  onChange,
  placeholder,
  readonly = false,
  minDate,
  maxDate,
  error,
  fullWidth = false,
  restrictToFutureTimes = false,
  biggerTimeSelector = false,
}: DateTimeSelectorProps) => {
  const dateComponent = date ? i18nDateToString(moment(date), 'ui') : undefined
  const time = date ? roundToNearest30Minutes(moment(date)).format('HH:mm') : undefined
  const availableTimeOptions = getAvailableTimeOptions(date, restrictToFutureTimes)

  const handleDateChange = (dateString?: string) => {
    if (!dateString) return

    const newDate = stringToDate(dateString, 'ui')
    const newAvailableOptions = getAvailableTimeOptions(newDate.toDate(), restrictToFutureTimes)

    if (date) {
      const currentTime = moment(date)
      const currentTimeString = currentTime.format('HH:mm')

      if (newAvailableOptions.includes(currentTimeString)) {
        newDate.set({
          hours: currentTime.hours(),
          minutes: currentTime.minutes(),
        })
      } else {
        const { hour, minute } = getFirstAvailableTime(newAvailableOptions)
        newDate.set({ hours: hour, minutes: minute })
      }
    } else {
      const { hour, minute } = getFirstAvailableTime(newAvailableOptions)
      newDate.set({ hours: hour, minutes: minute })
    }

    onChange?.(newDate.startOf('minute').toDate())
  }

  const handleTimeChange = (timeString?: string) => {
    if (!timeString || !date) return

    const parsedTime = parseTimeString(timeString)
    if (!parsedTime) return

    const newDate = moment(date)
    newDate.set({
      hours: parsedTime.hour,
      minutes: parsedTime.minute,
    })

    onChange?.(newDate.toDate())
  }

  const timeOptions: TimeOption[] = availableTimeOptions.map((timeValue) => ({
    label: timeValue,
    value: timeValue,
  }))

  return (
    <Box flexDirection="column" gap="s6">
      {error && (
        <Text size="200" color="radicalRed1000">
          {error}
        </Text>
      )}

      <Box flexDirection="row" gap="s6" alignItems="center">
        <Box flexGrow={fullWidth}>
          <DayPicker
            mandatory={mandatory}
            readonly={readonly}
            placeholder={placeholder}
            minDate={minDate ? moment(minDate) : null}
            maxDate={maxDate ? moment(maxDate) : null}
            onChange={handleDateChange}
            value={dateComponent}
          />
        </Box>
        <Box flexGrow={biggerTimeSelector} flexDirection="column" gap="s6">
          <Select
            none="--:--"
            readonly={readonly || !date}
            options={timeOptions}
            value={time}
            onChange={handleTimeChange}
          />
        </Box>
      </Box>
    </Box>
  )
}
